/**
 * Authentication API Contract Schemas
 *
 * These Zod schemas define the API contracts for authentication endpoints.
 * They serve as the single source of truth for:
 * - Backend request validation
 * - Frontend/Mobile form validation
 * - TypeScript type definitions
 *
 * @module schemas/auth
 */

import { z } from 'zod';
import { phoneSchema } from '../utils/validation';

// ============================================================================
// REGISTRATION
// ============================================================================

/**
 * User registration request schema
 *
 * Validation rules:
 * - email: Must be a valid email format
 * - password: Minimum 8 characters, must contain:
 *   - At least one lowercase letter (a-z)
 *   - At least one uppercase letter (A-Z)
 *   - At least one number (0-9)
 * - firstName: Minimum 2 characters
 * - lastName: Minimum 2 characters
 * - phone: Optional, but if provided must be a valid Indian mobile number (10 digits, starting with 6-9, e.g. 9876543210)
 * - acceptedTerms: Must be true (user must accept Terms and Privacy Policy)
 */
export const RegisterRequestSchema = z.object({
  email: z.string().email('Invalid email format'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  phone: phoneSchema.optional(),
  referralCode: z
    .string()
    .length(8, 'Referral code must be 8 characters')
    .regex(/^[A-Z0-9]{8}$/, 'Referral code must contain only uppercase letters and numbers')
    .optional(),
  acceptedTerms: z.literal(true, {
    errorMap: () => ({ message: 'You must accept the Terms and Privacy Policy' }),
  }),
});

export type RegisterRequest = z.infer<typeof RegisterRequestSchema>;

/**
 * User registration response schema
 */
export const RegisterResponseSchema = z.object({
  message: z.string(),
  user: z.object({
    id: z.string(),
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    role: z.enum(['CUSTOMER', 'VENDOR', 'VENDOR_STAFF', 'ADMIN', 'SUPER_ADMIN']),
    isEmailVerified: z.boolean(),
    createdAt: z.string(),
  }),
});

export type RegisterResponse = z.infer<typeof RegisterResponseSchema>;

// ============================================================================
// LOGIN
// ============================================================================

/**
 * User login request schema
 *
 * Validation rules:
 * - email: Must be a valid email format
 * - password: Required, no specific validation (checked against hash)
 */
export const LoginRequestSchema = z.object({
  email: z.string().email('Invalid email format'),
  password: z.string().min(1, 'Password is required'),
});

export type LoginRequest = z.infer<typeof LoginRequestSchema>;

/**
 * User login response schema
 */
export const LoginResponseSchema = z.object({
  access_token: z.string(),
  user: z.object({
    id: z.string(),
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    role: z.enum(['CUSTOMER', 'VENDOR', 'VENDOR_STAFF', 'ADMIN', 'SUPER_ADMIN']),
    isEmailVerified: z.boolean(),
  }),
});

export type LoginResponse = z.infer<typeof LoginResponseSchema>;

// ============================================================================
// PASSWORD RESET
// ============================================================================

/**
 * Request password reset email schema
 *
 * Validation rules:
 * - email: Must be a valid email format
 */
export const ForgotPasswordRequestSchema = z.object({
  email: z.string().email('Invalid email format'),
});

export type ForgotPasswordRequest = z.infer<typeof ForgotPasswordRequestSchema>;

/**
 * Reset password with token schema
 *
 * Validation rules:
 * - token: Required, generated by backend
 * - newPassword: Same rules as registration password
 */
export const ResetPasswordRequestSchema = z.object({
  token: z.string().min(1, 'Reset token is required'),
  newPassword: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
});

export type ResetPasswordRequest = z.infer<typeof ResetPasswordRequestSchema>;

// ============================================================================
// EMAIL VERIFICATION
// ============================================================================

/**
 * Verify email with token schema
 */
export const VerifyEmailRequestSchema = z.object({
  token: z.string().min(1, 'Verification token is required'),
});

export type VerifyEmailRequest = z.infer<typeof VerifyEmailRequestSchema>;

/**
 * Resend verification email schema
 */
export const ResendVerificationRequestSchema = z.object({
  email: z.string().email('Invalid email format'),
});

export type ResendVerificationRequest = z.infer<typeof ResendVerificationRequestSchema>;

// ============================================================================
// PASSWORD CHANGE (Authenticated User)
// ============================================================================

/**
 * Change password for authenticated user schema
 *
 * Validation rules:
 * - currentPassword: Required
 * - newPassword: Same rules as registration password
 */
export const ChangePasswordRequestSchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required'),
  newPassword: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
});

export type ChangePasswordRequest = z.infer<typeof ChangePasswordRequestSchema>;

// ============================================================================
// GOOGLE OAUTH
// ============================================================================

/**
 * Google OAuth callback schema
 */
export const GoogleOAuthCallbackSchema = z.object({
  code: z.string().min(1, 'OAuth code is required'),
  redirectUri: z.string().url('Invalid redirect URI'),
});

export type GoogleOAuthCallback = z.infer<typeof GoogleOAuthCallbackSchema>;

// ============================================================================
// APPLE SIGN-IN (Mobile)
// ============================================================================

/**
 * Apple Sign-In mobile auth request schema
 *
 * Validation rules:
 * - identityToken: JWT identity token from Apple (required)
 * - authorizationCode: Short-lived authorization code from Apple (optional)
 * - user: Apple user ID (sub claim from the decoded JWT) (required)
 * - fullName: User's name components — only provided on first authorization (optional)
 * - email: User's email — only sent by Apple on first authorization (optional)
 */
export const AppleMobileAuthRequestSchema = z.object({
  identityToken: z.string().min(1),
  authorizationCode: z.string().optional(),
  user: z.string().min(1), // Apple user ID (sub claim from JWT)
  fullName: z
    .object({
      givenName: z.string().nullable().optional(),
      familyName: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  email: z.string().email().nullable().optional(), // Only sent on first authorization
});

export type AppleMobileAuthRequest = z.infer<typeof AppleMobileAuthRequestSchema>;
